/**
 * React hook that builds the {@link ApiClient} bound to the deployed API endpoint, the current
 * user's **ID token**, and the selected Account. The ID token — not the access token — is the bearer
 * credential: the Cognito Pre-Token-Generation trigger emits the `accountId`/`role` claims the
 * Soroush_API authorizes on onto ID tokens only (see packages/iac/src/constructs/cognito-identity.ts),
 * so an access token authenticates but then fails authorization (401/403) on every read. The token is
 * read lazily on each request from the OIDC auth state, so token refreshes are picked up without
 * rebuilding the client.
 *
 * ## The Account is in the memo key deliberately, and that is the switch mechanism
 *
 * Memoized on the endpoint, the current user, **and the selected Account** (Req 5.8). The account
 * getter is lazy like the token's, so the header alone would be correct without the dependency — but
 * every page keys its fetching effect on the client identity, so leaving it out would send the right
 * header and refetch nothing. The dashboard would go on showing the previous Account's Checks under the
 * new Account's label, which is the one outcome a switcher must never produce.
 */

import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { useSelectedAccount } from '../account-context';
import { useConfig } from '../config-context';

import { createApiClient, type ApiClient } from './client';

export function useApiClient(): ApiClient {
  const { apiEndpoint } = useConfig();
  const auth = useAuth();
  const user = auth.user;
  const { accountId } = useSelectedAccount();

  return useMemo(
    // `undefined` for `fetchImpl` takes the default `fetch`; the account getter is the fourth
    // parameter because appending kept every existing caller and test untouched.
    () => createApiClient(apiEndpoint, () => user?.id_token, undefined, () => accountId),
    [apiEndpoint, user, accountId],
  );
}
